/** German texts of the module, below its ID: `appointments.nav.appointments`. */
export const de = {
  name: 'Termine',
  description:
    'Kunden- und Projekttermine, einmalig oder im Turnus, mit Kalender, Karte und Besetzung. Eingetragene bekommen den Termin in ihren Outlook-Kalender.',
  nav: { appointments: 'Termine', requests: 'Terminanfragen', report: 'Auswertung' },
  entities: { appointment: 'Termin', request: 'Terminanfrage' },
  permissions: {
    appointment: {
      read: {
        label: 'Termine ansehen',
        description:
          'Liste, Kalender und Karte der Termine öffnen. Mit Umfang „Eigene“ nur Termine, bei denen die Person eingetragen ist oder die sie angelegt hat.',
      },
      join: {
        label: 'Für Termine eintragen',
        description: 'Sich selbst für Termine eintragen und wieder austragen.',
      },
      write: {
        label: 'Termine planen',
        description:
          'Termine und Turnusse anlegen, ändern, Personen einteilen und Termine absagen. Mit Umfang „Eigene“ nur eigene Termine.',
      },
      delete: { label: 'Termine löschen', description: 'Termine und Turnusse löschen.' },
      comment: {
        label: 'Termine kommentieren',
        description: 'Kommentare an Terminen schreiben.',
      },
      report: {
        label: 'Terminauswertung',
        description:
          'Stunden der Termine eines Zeitraums auswerten und als CSV-Datei exportieren, z. B. für die Abrechnung.',
      },
    },
    request: {
      manage: {
        label: 'Terminanfragen bearbeiten',
        description:
          'Anfragen aus E-Mails an die Adresse der Firma sehen, als Termin übernehmen oder ablehnen. Zum Übernehmen braucht es zusätzlich „Termine planen“.',
      },
    },
    settings: {
      manage: {
        label: 'Termine einrichten',
        description: 'Terminarten und Funktionen der Firma pflegen.',
      },
    },
  },
  roles: {
    participant: 'Termine: Ansehen und eintragen',
    planner: 'Termine: Planen',
    manager: 'Termine: Verwalten',
  },
  sections: { appointments: 'Termine' },
  dashboard: { mine: 'Meine Termine' },
  catalogs: {
    types: {
      label: 'Terminarten',
      description: 'Arten von Terminen, z. B. Begehung, Kundentermin oder Abnahme.',
    },
    functions: {
      label: 'Funktionen',
      description:
        'Wer für einen Termin gebraucht wird, z. B. Brandschutzbeauftragte/r oder SiGeKo.',
    },
  },
  notifications: {
    assigned: 'Du wurdest für einen Termin eingeteilt',
    request: 'Neue Terminanfrage per E-Mail',
  },
  ai: { readMail: 'Termin aus E-Mail auslesen' },
  audit: {
    appointment: {
      create: 'Termin angelegt',
      update: 'Termin geändert',
      delete: 'Termin gelöscht',
      join: 'Für Termin eingetragen',
      leave: 'Vom Termin ausgetragen',
      cancel: 'Termin abgesagt',
      restore: 'Termin wieder aufgenommen',
    },
    series: { extend: 'Turnus fortgeschrieben' },
    request: {
      create: 'Terminanfrage eingegangen',
      accept: 'Terminanfrage übernommen',
      decline: 'Terminanfrage abgelehnt',
    },
  },
  guides: {
    appointments: {
      title: 'Termine planen und besetzen',
      summary: 'Termine und Turnusse anlegen, sich eintragen, Outlook und wer was darf.',
    },
  },
  errors: {
    title: 'Bitte einen Titel eingeben (höchstens 200 Zeichen).',
    date: 'Bitte ein gültiges Datum eingeben.',
    time: 'Bitte Beginn und Ende als Uhrzeit eingeben.',
    endBeforeStart: 'Das Ende muss nach dem Beginn liegen.',
    tooLong: 'Ein Termin dauert höchstens 31 Tage.',
    range: 'Der Zeitraum ist ungültig oder länger als 400 Tage.',
    type: 'Bitte eine aktive Terminart wählen.',
    function: 'Bitte eine aktive Funktion wählen.',
    project:
      'Bitte ein Projekt der Firma wählen, das du sehen darfst und das nicht archiviert ist.',
    projectArchived: 'Das Projekt ist archiviert.',
    object: 'Bitte ein Objekt wählen, das in Projekte verfügbar und nicht archiviert ist.',
    objectOfProject: 'Das Objekt gehört nicht zu diesem Projekt.',
    place: 'Der Ort ist unvollständig. Bitte die Adresse neu suchen oder eingeben.',
    placeEmpty: 'Bitte eine Bezeichnung, Straße, einen Ort oder einen Punkt auf der Karte angeben.',
    placeObject: 'Für „Am Objekt“ bitte zuerst ein Objekt wählen.',
    placeLabel: 'Die Bezeichnung des Orts darf höchstens 200 Zeichen lang sein.',
    street: 'Straße und Hausnummer sind zu lang.',
    postcode: 'Die Postleitzahl ist zu lang.',
    city: 'Der Ort ist zu lang.',
    country: 'Bitte einen gültigen Ländercode angeben, z. B. DE.',
    meetingUrl: 'Bitte einen Link angeben, der mit https:// beginnt.',
    description: 'Die Beschreibung darf höchstens 5.000 Zeichen lang sein.',
    staff: 'Bitte aktive Konten der Firma wählen.',
    tooManyStaff: 'Höchstens 20 Personen je Termin.',
    guest: 'Die Person ohne Konto ist ungültig.',
    guestName: 'Bitte einen Namen eingeben (höchstens 100 Zeichen).',
    guestEmail: 'Bitte eine gültige E-Mail-Adresse eingeben oder das Feld leer lassen.',
    staffNeeded: 'Bitte eine Zahl von 0 bis 20 eingeben.',
    repeat: 'Bitte eine Wiederholung wählen.',
    repeatUntil: 'Bitte ein gültiges Enddatum eingeben.',
    repeatCount: 'Bitte eine Anzahl von 2 bis 500 eingeben.',
    repeatEnd: 'Bitte entweder ein Enddatum oder eine Anzahl angeben.',
    repeatScope: 'Die Wiederholung lässt sich nur für diesen und alle folgenden Termine ändern.',
    slotDay: 'Bitte einen Tag im Zeitfenster des Termins wählen.',
    slotPast: 'Der Tag liegt in der Vergangenheit.',
  },
  pages: {
    allDay: 'ganztägig',
    days: '{{from}} bis {{to}}',
    today: 'Heute',
    nobody: 'noch niemand',
    online: 'Online',
    series: 'Turnus',
    dayOpen: 'Tag offen',
    dayOpenWindow: 'Tag offen, {{from}} bis {{to}}',
    slot: {
      person: 'Wer geht hin',
      assign: 'Jemand anderem zuweisen',
      kind: 'Person',
      account: 'Mit Konto',
      guest: 'Ohne Konto',
      guestName: 'Name',
      guestNamePlaceholder: 'z. B. Max Muster, Muster Bau GmbH',
      guestEmail: 'E-Mail',
      guestEmailHint: 'Mit Adresse kommt die Einladung per E-Mail, ohne Adresse keine.',
      hintGuest: 'Die Person wird mit Namen eingetragen.',
      moreLabel: 'Weitere Möglichkeiten für „{{title}}“',
      hintOther:
        'Die Person wird eingetragen und bekommt eine Nachricht und die Einladung per E-Mail.',
      title: 'Termin übernehmen',
      changeTitle: 'Tag ändern',
      description:
        '„{{title}}“ findet einmal zwischen {{from}} und {{to}} statt. Wähl Tag und Uhrzeit.',
      day: 'Tag',
      hint: 'Du wirst eingetragen und bekommst die Einladung per E-Mail.',
      noDays: 'Im Zeitfenster ist kein Tag mehr frei.',
      submit: 'Übernehmen',
      saved: '„{{title}}“ ist für {{day}} festgelegt.',
      take: 'Übernehmen',
      takeLabel: '„{{title}}“ übernehmen und Tag festlegen',
      choose: 'Tag festlegen',
      chooseLabel: 'Tag für „{{title}}“ festlegen',
      change: 'Tag ändern',
      clear: 'Tag wieder offen',
      cleared: 'Der Tag ist wieder offen.',
      window: 'Zeitfenster',
      windowText: '{{from}} bis {{to}}',
    },
    fields: { place: 'Ort', project: 'Projekt' },
    staffing: {
      none: 'Unbesetzt, {{needed}} gebraucht',
      partly: '{{taken}} von {{needed}} besetzt',
      full: 'Besetzt',
      cancelled: 'Abgesagt',
    },
    legend: {
      label: 'Farben',
      none: 'unbesetzt',
      partly: 'teilweise besetzt',
      full: 'besetzt',
      cancelled: 'abgesagt',
    },
    join: {
      join: 'Ich bin dabei',
      leave: 'Austragen',
      joinLabel: 'Für „{{title}}“ eintragen',
      leaveLabel: 'Von „{{title}}“ austragen',
      joined: 'Du bist für „{{title}}“ eingetragen. Die Einladung kommt per E-Mail.',
      left: 'Du bist von „{{title}}“ ausgetragen.',
    },
    rule: {
      daily_one: 'Täglich',
      daily_other: 'Alle {{count}} Tage',
      weekly_one: 'Jede Woche',
      weekly_other: 'Alle {{count}} Wochen',
      monthly_one: 'Jeden Monat',
      monthly_other: 'Alle {{count}} Monate',
      yearly_one: 'Jedes Jahr',
      yearly_other: 'Alle {{count}} Jahre',
      on: 'am {{days}}',
      until: 'bis {{day}}',
      count_one: '{{count}} Termin',
      count_other: '{{count}} Termine',
    },
    list: {
      title: 'Termine',
      description:
        'Termine dieser und der nächsten zwei Wochen oder eines anderen Zeitraums. Outlook-Termine oder E-Mails hierher ziehen, um daraus einen Termin anzulegen.',
      descriptionReadOnly:
        'Termine dieser und der nächsten zwei Wochen oder eines anderen Zeitraums.',
      new: 'Neuer Termin',
      previous: 'Früherer Zeitraum',
      next: 'Späterer Zeitraum',
      today: 'Heute',
      period: '{{from}} bis {{to}}',
      span: 'Zeitraum',
      spans: {
        s1: '1 Woche',
        s2: '2 Wochen',
        s3: '3 Wochen',
        s4: '4 Wochen',
        smonth: 'Monat',
        scustom: 'Eigener Zeitraum',
      },
      periodFrom: 'Von',
      periodTo: 'Bis',
      periodInvalid: 'Bitte ein Ende nach dem Beginn wählen, höchstens ein Jahr später.',
      nobodyYet: 'noch niemand',
      views: 'Darstellung',
      viewList: 'Liste',
      viewCalendar: 'Kalender',
      viewMap: 'Karte',
      who: 'Welche Termine',
      whoAll: 'Alle',
      whoMine: 'Meine',
      whoOpen: 'Unbesetzt',
      search: 'Titel, Ort, Projekt oder Person suchen',
      searchPlaceholder: 'Titel, Ort, Person …',
      type: 'Terminart',
      allTypes: 'Alle Terminarten',
      function: 'Funktion',
      allFunctions: 'Alle Funktionen',
      count_one: '{{count}} Termin',
      count_other: '{{count}} Termine',
      unstaffed_one: '{{count}} braucht noch Leute',
      unstaffed_other: '{{count}} brauchen noch Leute',
      allStaffed: 'Alle besetzt',
      empty: 'Keine Termine in diesem Zeitraum',
      emptyHint: 'Lege einen Termin an oder zieh einen Outlook-Termin hierher.',
      emptyReadOnly: 'Sobald Termine geplant sind, erscheinen sie hier.',
      noMatches: 'Keine passenden Termine',
      noneOpen: 'Alles besetzt',
      noneOpenHint: 'In diesem Zeitraum hat jeder Termin genug Leute.',
      calendarLabel: 'Kalender der Termine, {{period}}',
      more: '+{{count}} weitere',
      dayEmpty: 'Keine Termine am {{day}}',
      mapLabel: 'Karte der Termine',
      mapEmpty: 'Keiner dieser Termine hat einen Ort mit Koordinaten.',
      mapMissing_one: '{{count}} Termin ohne Ort steht nur in der Liste.',
      mapMissing_other: '{{count}} Termine ohne Ort stehen nur in der Liste.',
      drop: {
        button: 'Aus Outlook übernehmen',
        overlay: 'Loslassen, um daraus einen Termin anzulegen',
        notice: 'Übernommen aus „{{name}}“. Bitte prüfen und ergänzen.',
        unreadable:
          'Aus dieser Datei lässt sich kein Termin lesen. Geht: Outlook-Termine und E-Mails (.msg, .eml) und Kalenderdateien (.ics).',
      },
    },
    form: {
      newTitle: 'Neuer Termin',
      newDescription: 'Einmalig oder im Turnus. Eingeteilte bekommen eine Einladung per E-Mail.',
      editTitle: 'Termin bearbeiten',
      editDescription: 'Eingetragene bekommen die Änderung in ihren Kalender.',
      create: 'Anlegen',
      created: 'Termin angelegt.',
      saved: 'Termin gespeichert.',
      scope: 'Was soll sich ändern?',
      scopeOne: 'Nur dieser Termin',
      scopeFollowing: 'Dieser und alle folgenden Termine',
      scopeFollowingHint: 'Einzeln geänderte Termine bleiben, wie sie sind.',
      title: 'Titel',
      titlePlaceholder: 'z. B. Begehung Kita Sonnenweg',
      type: 'Terminart',
      function: 'Gebraucht wird',
      functionHint: 'Funktion, die den Termin wahrnimmt, z. B. SiGeKo.',
      when: 'Wann',
      timing: 'Art des Termins',
      timingTime: 'Mit Uhrzeit',
      timingAllDay: 'Ganztägig',
      timingOpen: 'Tag offen',
      windowFrom: 'Frühestens am',
      windowTo: 'Spätestens am',
      windowHint:
        'Der Termin findet einmal in diesem Zeitraum statt. Wer ihn übernimmt, legt Tag und Uhrzeit fest. Mit „Wöchentlich“ wiederholt sich das Zeitfenster jede Woche.',
      date: 'Datum',
      endDay: 'Bis einschließlich',
      from: 'Von',
      to: 'Bis',
      repeat: 'Wiederholen',
      repeatNever: 'Nicht wiederholen',
      presets: {
        daily: 'Täglich',
        workdays: 'Jeden Werktag (Mo–Fr)',
        weekly: 'Jede Woche am {{day}}',
        biweekly: 'Alle 2 Wochen am {{day}}',
        monthly: 'Jeden Monat {{on}}',
        quarterly: 'Alle 3 Monate {{on}}',
        yearly: 'Jedes Jahr am {{date}}',
        custom: 'Eigene Regel …',
      },
      every: 'Wiederholen alle',
      unit: 'Einheit',
      units: {
        daily_one: 'Tag',
        daily_other: 'Tage',
        weekly_one: 'Woche',
        weekly_other: 'Wochen',
        monthly_one: 'Monat',
        monthly_other: 'Monate',
        yearly_one: 'Jahr',
        yearly_other: 'Jahre',
      },
      weekdays: 'An diesen Tagen',
      weekdaysHint: 'Ohne Auswahl am Wochentag des ersten Termins ({{day}}).',
      monthly: 'Im Monat',
      monthlyOn: {
        day: 'am {{date}}.',
        weekday: 'am {{nth}} {{day}}',
        lastWeekday: 'am letzten {{day}}',
        lastDay: 'am letzten Tag des Monats',
      },
      nth: { n1: 'ersten', n2: 'zweiten', n3: 'dritten', n4: 'vierten' },
      repeatEnd: 'Endet',
      repeatEndNever: 'Nie',
      repeatEndUntil: 'Am Datum',
      repeatEndCount: 'Nach Anzahl',
      until: 'Letzter möglicher Tag',
      count: 'Anzahl Termine',
      nextDates: 'Nächste Termine: {{dates}}',
      where: 'Wo',
      project: 'Projekt',
      object: 'Objekt',
      place: 'Ort',
      placeKinds: { object: 'Am Objekt', address: 'Eigene Adresse', none: 'Kein Ort' },
      placeObjectHint: 'Der Termin findet an der Adresse des Objekts statt.',
      placeObjectMissing: 'Bitte oben ein Objekt wählen.',
      addressSearch: 'Adresse suchen',
      addressSearchHint: 'Füllt die Felder unten und zeigt den Termin auf der Karte.',
      placeLabel: 'Bezeichnung',
      placeLabelPlaceholder: 'z. B. Rathaus, Sitzungssaal 2',
      street: 'Straße',
      houseNumber: 'Nr.',
      postcode: 'PLZ',
      city: 'Ort',
      online: 'Online-Termin',
      onlineHint: 'Teams, Zoom oder ein anderes Online-Meeting.',
      meetingUrl: 'Link zum Meeting',
      meetingUrlPlaceholder: 'https://teams.microsoft.com/…',
      meetingUrlHint: 'Steht in der Einladung und auf der Terminseite.',
      who: 'Wer',
      staffNeeded: 'Benötigt',
      staffNeededHint: 'Personen',
      staff: 'Eingeteilt',
      staffHint: 'Weitere tragen sich selbst ein.',
      staffSeriesHint: 'Diese Personen sind bei jedem Termin des Turnus dabei.',
      guests: 'Ohne Konto',
      guestsHint:
        'Für Leute, die die App nicht nutzen. Mit E-Mail-Adresse bekommen sie die Einladung per E-Mail, ohne Adresse keine.',
      guestsAdd: 'Person ohne Konto',
      guestRow: 'Person ohne Konto {{number}}',
      guestRemove: 'Person ohne Konto {{number}} entfernen',
      guestName: 'Name',
      guestEmail: 'E-Mail (optional)',
      description: 'Beschreibung',
      descriptionPlaceholder: 'Ablauf, Ansprechpartner vor Ort, Zugang …',
    },
    detail: {
      notFound: 'Termin nicht gefunden',
      notFoundHint: 'Er wurde gelöscht, oder du darfst ihn nicht sehen.',
      when: 'Wann',
      series: 'Turnus',
      download: 'In Kalender laden',
      cancel: 'Absagen',
      restore: 'Wieder aufnehmen',
      cancelled: 'Termin abgesagt. Eingetragene bekommen die Absage.',
      restored: 'Termin wieder aufgenommen.',
      cancelledTitle: 'Dieser Termin ist abgesagt',
      cancelledHint: 'Er bleibt sichtbar, niemand kann sich mehr eintragen.',
      cancelTitle: 'Termin absagen?',
      cancelDescription:
        '„{{title}}“ wird abgesagt. Alle Eingetragenen bekommen die Absage in ihren Kalender.',
      deleteTitle: 'Termin löschen?',
      deleteDescription:
        '„{{title}}“ wird mit Kommentaren gelöscht. Eingetragene bekommen eine Absage.',
      deleteScope: 'Welche Termine löschen',
      deleteOne: 'Nur diesen Termin',
      deleteFollowing: 'Diesen und alle folgenden des Turnus',
      deleted: 'Termin gelöscht.',
      whereTitle: 'Wann und wo',
      place: 'Ort',
      noPlace: 'Kein Ort angegeben',
      online: 'Online',
      joinMeeting: 'Am Meeting teilnehmen',
      onlineNoLink: 'Link folgt',
      project: 'Projekt',
      object: 'Objekt',
      staffTitle: 'Besetzung',
      staffing: 'Stand',
      function: 'Gebraucht wird',
      staff: 'Eingetragen',
      guest: 'ohne Konto',
      invites: 'Outlook',
      invitesHint:
        'Eingetragene bekommen eine Einladung per E-Mail, höchstens vier Wochen vor dem Termin.',
      description: 'Beschreibung',
      map: 'Karte',
      activity: 'Kommentare und Verlauf',
      changed: 'Zuletzt geändert {{when}}',
    },
    report: {
      title: 'Auswertung',
      description:
        'Stunden der Termine in einem Zeitraum, z. B. für die Abrechnung: jeder Termin mit seiner Dauer und die Summe. Abgesagte Termine zählen nicht.',
      caption: 'Termine {{period}}',
      previous: 'Früherer Zeitraum',
      next: 'Späterer Zeitraum',
      thisMonth: 'Dieser Monat',
      span: 'Zeitraum',
      spans: { month: 'Monat', custom: 'Eigener Zeitraum' },
      project: 'Projekt',
      allProjects: 'Alle Projekte',
      searchProject: 'Projekt suchen: Nummer oder Name',
      noProject: 'Kein Projekt gefunden',
      totalRow: 'Summe',
      withoutProject: 'Ohne Projekt',
      withoutProjectHint_one: 'Ein Termin hat kein Projekt. Für die Abrechnung bitte zuordnen.',
      withoutProjectHint_other:
        '{{count}} Termine haben kein Projekt. Für die Abrechnung bitte zuordnen.',
      showWithoutProject: 'Anzeigen',
      assign: {
        button: 'Zuordnen',
        label: 'Projekt für „{{title}}“ zuordnen',
        title: 'Projekt zuordnen',
        project: 'Projekt',
        projectMissing: 'Bitte ein Projekt wählen.',
        object: 'Objekt',
        following: 'Auch alle folgenden Termine des Turnus',
        followingHint: 'Einzeln geänderte Termine bleiben, wie sie sind.',
        saved: '„{{title}}“ ist zugeordnet.',
      },
      totalHours: 'Stunden gesamt',
      hoursUnit: 'Std.',
      count: 'Termine',
      withoutHours: 'Ohne Uhrzeit',
      withoutHoursHint:
        'Ganztägige Termine und Termine mit offenem Tag haben keine Uhrzeit und zählen nicht zu den Stunden.',
      noHours: 'ohne Uhrzeit',
      columns: {
        day: 'Datum',
        time: 'Zeit',
        title: 'Termin',
        project: 'Projekt',
        staff: 'Übernommen von',
        hours: 'Stunden',
      },
      empty: 'Keine Termine in diesem Zeitraum',
      emptyHint: 'Wähle einen anderen Monat oder einen eigenen Zeitraum.',
      fileName: 'Terminauswertung {{period}}',
    },
    section: {
      new: 'Neuer Termin',
      empty: 'Keine anstehenden Termine',
      emptyHint: 'Lege den ersten Termin an.',
      emptyReadOnly: 'Sobald Termine geplant sind, erscheinen sie hier.',
      more_one: 'Ein weiterer Termin steht in der Terminliste.',
      more_other: '{{count}} weitere Termine stehen in der Terminliste.',
    },
    tiles: {
      mineEmpty: 'Keine Termine in den nächsten zwei Wochen',
      mineEmptyHint: 'Trag dich unter Termine für einen Termin ein.',
    },
    requests: {
      title: 'Terminanfragen',
      description: 'Termine, die per E-Mail an die Adresse der Firma kamen und noch jemand prüft.',
      addressTitle: 'Adresse für Termine',
      addressHint:
        'Leite Einladungen und Mails mit Terminen an diese Adresse weiter. Kommt die Mail von deinem Konto, legt die App den Termin gleich an. Alle anderen Mails landen hier.',
      address: 'E-Mail-Adresse',
      copy: 'Adresse kopieren',
      copied: 'Adresse kopiert',
      status: 'Anzeigen',
      open: 'Offen',
      done: 'Erledigt',
      from: 'Von {{sender}}',
      fromAccount: 'Konto der Firma',
      received: 'Eingegangen {{when}}',
      noSubject: '(ohne Betreff)',
      proposal: 'Vorschlag',
      noProposal:
        'Keine Zeit erkannt. Trag sie beim Übernehmen ein oder lass die KI die Mail lesen.',
      allDay: 'ganztägig',
      text: 'Text der Mail',
      accept: 'Übernehmen',
      read: 'Mit KI auslesen',
      readDone: 'Vorschlag aktualisiert',
      decline: 'Ablehnen',
      declineTitle: 'Terminanfrage ablehnen?',
      declineDescription:
        'Die Anfrage „{{subject}}“ wird als abgelehnt abgelegt. Der Absender bekommt keine Nachricht.',
      declined: 'Terminanfrage abgelehnt',
      acceptNotice: 'Aus der Mail von {{sender}}. Prüfe die Angaben, bevor du speicherst.',
      accepted: 'Übernommen',
      declinedBadge: 'Abgelehnt',
      openAppointment: 'Termin öffnen',
      empty: 'Keine offenen Terminanfragen',
      emptyHint: 'Mails an die Adresse oben erscheinen hier.',
      emptyDone: 'Noch nichts erledigt',
      emptyDoneHint: 'Übernommene und abgelehnte Anfragen stehen hier 30 Tage lang.',
    },
    errors: {
      invalidInput: 'Bitte die markierten Felder prüfen.',
      versionConflict:
        'Der Termin wurde inzwischen geändert. Bitte die Seite neu laden und noch einmal versuchen.',
      cancelled: 'Der Termin ist abgesagt.',
      past: 'Der Termin ist schon vorbei.',
      full: 'Für diesen Termin sind schon 20 Personen eingetragen.',
      notFound: 'Den Termin gibt es nicht mehr.',
      forbidden: 'Dafür fehlt dir die Berechtigung.',
      slotTaken: 'Den Termin hat schon jemand übernommen.',
      dayFixed: 'Dieser Termin hat einen festen Tag.',
      requestDecided: 'Über diese Anfrage hat schon jemand entschieden.',
      ai: 'Die KI konnte die Mail gerade nicht lesen. Bitte später noch einmal versuchen.',
    },
  },
};
